'use client';

import type {FormEvent} from 'react';
import {type FC, useCallback, useId, useState} from 'react';
import clsx from 'clsx';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';

import type {Filters} from '@/components/article-search-filters/search-params-filter';
import {
	filtersToSearchParams,
	searchParamsToFilters,
} from '@/components/article-search-filters/search-params-filter';
import {buttonClasses} from '@/components/button';
import {searchInputClasses, SearchInputPrefix} from '@/components/input';
import {i18n} from '@/utils/i18n';

interface Props {
	placeholder?: string;
}

export const ArticleSearchInputClient: FC<Props> = ({placeholder}) => {
	const id = useId();
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();

	const qSearchParams = searchParams.get('q');
	const [value, setValue] = useState(
		typeof qSearchParams === 'string' ? qSearchParams : '',
	);

	const filters: Filters = searchParamsToFilters(searchParams);

	const handleSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			const searchQuery = value.trim();

			// FIXME: fix react hook error
			// eslint-disable-next-line react-hooks/immutability
			filters.q = searchQuery;

			const searchParams = filtersToSearchParams(filters);

			router.push(`${pathname}?${searchParams.toString()}`, {
				scroll: false,
			});
		},
		[filters, pathname, router, value],
	);

	return (
		<form
			className={clsx('flex', 'gap-4', 'sm:gap-10')}
			onSubmit={handleSubmit}
		>
			{}
			<label
				className={clsx(
					'flex-1',
					'relative',
					'max-w-sm',
					'block',
					'w-full',
				)}
			>
				<input
					id={id}
					type="text"
					name="q"
					placeholder={placeholder}
					value={value}
					onChange={(event) =>
						setValue && setValue(event.target.value)
					}
					className={clsx(searchInputClasses)}
				/>
				<SearchInputPrefix />
			</label>

			<button
				type="submit"
				className={clsx(
					buttonClasses,
					'text-beige-500',
					'rounded-lg',
					'!text-base',
					'!font-medium',
					'!h-12',
					'!px-10',
				)}
			>
				{i18n.components.search.buttonArticleSearch}
			</button>
		</form>
	);
};
